<template>
  <main v-if="home_is_ready" class="flex flex-col h-full max-h-full overflow-hidden focus:outline-none">
    <div class="absolute flex h-10 gap-2 top-2 right-2 w-fit">
      <button v-if="current_step != 'object-selection'" class="w-max cancel-btn" :disabled="syncing_is_on" @click="goBack">
        <ArrowLeft
          class="w-5 h-5 mr-3 -ml-1"
          :class="[{ 'rotate-180': $store.state.portfolio.ui_options?.language == 'ar' }]"
          aria-hidden="true" />
        {{ $t("Go back") }} {{ current_step == "object-type" ? $t("to object selection") : "" }}
      </button>
      <button :disabled="syncing_is_on" class="w-max cancel-btn" @click="cancelIntegration">{{ $t("Close") }}</button>
      <button
        v-if="current_step == 'mapping'"
        type="button"
        :disabled="syncing_is_on"
        class="m-0 primary-outline-btn w-fit"
        @click="
          () => {
            $emitter.emit(`${ALL_EMITTERS.SELECT_OTHER_OBJECT}${integration_name.capitalize()}`)
          }
        ">
        <CirclePlus class="w-5 h-5 mr-3 -ml-1" aria-hidden="true" />
        <span> {{ $t("Map more objects") }} </span>
      </button>
      <button
        v-if="current_step == 'mapping' || is_selected_object"
        type="button"
        :disabled="syncing_is_on"
        class="m-0 primary-btn w-fit"
        @click="
          () => {
            syncing_is_on = true
            $emitter.emit(`${ALL_EMITTERS.START_SYNCING}${integration_name.capitalize()}`)
          }
        ">
        <RotateCw :class="[syncing_is_on ? 'animate-spin' : '']" class="w-5 h-5 mr-3 -ml-1" aria-hidden="true" />
        <span v-if="syncing_is_on"> {{ $t("Submitting...") }} </span>
        <span v-else> {{ $t("Submit") }} </span>
      </button>
    </div>

    <!-- Main Content -->
    <TabGroup
      :selected-index="current_step_index"
      as="div"
      class="flex flex-col w-full h-full bg-gray-50"
      @change="current_step_index = $event">
      <nav aria-label="Progress" class="w-full bg-white border-b border-gray-200">
        <div class="max-w-6xl mx-auto px-6 py-8">
          <!-- Progress Steps Container -->
          <div class="relative">
            <!-- Main Progress Bar -->
            <div class="absolute top-5 left-0 w-full h-1">
              <div class="h-full bg-gray-200 rounded-full" />
              <div
                class="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all duration-500"
                :style="{ width: `${Math.min((current_step_index / (steps.filter((s) => !s.hidden).length - 1)) * 100, 100)}%` }" />
            </div>
            <!-- Steps -->
            <div class="relative flex justify-between">
              <div v-for="(step, stepIdx) in steps.filter((s) => !s.hidden)" :key="step.name" class="flex flex-col items-center">
                <!-- Step Marker -->
                <div
                  class="relative flex items-center justify-center w-10 h-10 rounded-full mb-3 transition-all duration-300"
                  :class="[
                    step.status === 'complete'
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-600 shadow-lg shadow-blue-100'
                      : step.status === 'current'
                        ? 'bg-white ring-4 ring-blue-500/30 border-2 border-blue-500'
                        : 'bg-white border-2 border-gray-200'
                  ]">
                  <!-- Step Content -->
                  <Check v-if="step.status === 'complete'" class="w-5 h-5 text-white" />
                  <div v-else-if="step.status === 'current'" class="w-2.5 h-2.5 rounded-full bg-blue-500" />
                  <span v-else class="text-sm font-medium" :class="step.status === 'upcoming' ? 'text-gray-400' : 'text-gray-600'">
                    {{ stepIdx + 1 }}
                  </span>

                  <!-- Animated Ring -->
                  <div v-if="step.status === 'current'" class="absolute -inset-1 rounded-full border-2 border-blue-500/30 animate-ping" />
                </div>

                <!-- Labels Container -->
                <div class="flex flex-col items-center space-y-1 w-32">
                  <!-- Step Name -->
                  <span
                    class="text-sm font-semibold text-center"
                    :class="[step.status === 'complete' ? 'text-blue-600' : step.status === 'current' ? 'text-blue-600' : 'text-gray-500']">
                    {{ step.name }}
                  </span>
                  <!-- Description -->
                  <span class="text-xs text-gray-400 text-center line-clamp-2 min-h-[2rem] relative group" :title="step.description">
                    {{ step.description }}
                    <div
                      class="hidden group-hover:block absolute bottom-full left-1/2 transform -translate-x-1/2 px-2 py-1 mb-1 bg-gray-900 text-white text-xs rounded whitespace-normal w-48 z-50">
                      {{ step.description }}
                      <div class="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                    </div>
                  </span>
                </div>

                <!-- Connection Line -->
                <div
                  v-if="stepIdx < steps.filter((s) => !s.hidden).length - 1"
                  class="absolute h-1 top-5 left-[calc(50%+20px)] right-[calc(50%+20px)]"
                  :class="[step.status === 'complete' ? 'bg-gradient-to-r from-blue-500 to-indigo-600' : 'bg-gray-200']" />
              </div>
            </div>
          </div>
        </div>
      </nav>

      <!-- Content Area -->
      <div class="flex-1 overflow-hidden bg-gray-50">
        <div class="h-full overflow-y-auto px-6 pb-6 mt-5">
          <div class="max-w-10xl mx-auto">
            <div class="bg-white rounded-xl shadow-sm border border-gray-100 px-5 py-8">
              <keep-alive>
                <component :is="getCurrentComponent" v-bind="getCurrentProps" v-on="getCurrentEvents" />
              </keep-alive>
            </div>
          </div>
        </div>
      </div>
    </TabGroup>
  </main>
</template>

<script>
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from "@headlessui/vue"
import Mapping from "@/views/dashboard/admin/integrations/data/steps/Mapping.vue"
import ObjectSelection from "@/views/dashboard/admin/integrations/data/steps/ObjectSelection.vue"
import MappingType from "@/views/dashboard/admin/integrations/data/steps/MappingType.vue"
import ObjectTypeSelect from "@/views/dashboard/admin/integrations/data/steps/ObjectTypeSelect.vue"
import Authorization from "@/views/dashboard/admin/integrations/data/steps/Authorization.vue"
import SpreadSheetUpload from "@/views/dashboard/admin/integrations/data/steps/SpreadSheetUpload.vue"
import IntegrationService from "@/services/integration.service"
import { Check, RotateCw, ArrowLeft, CirclePlus } from "lucide-vue-next"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { getServiceTitle } from "@/views/dashboard/admin/integrations/ml.js"

export default {
  components: {
    TabGroup,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    RotateCw,
    ObjectSelection,
    MappingType,
    ObjectTypeSelect,
    Mapping,
    Authorization,
    SpreadSheetUpload,
    CirclePlus,
    ArrowLeft,
    Check
  },

  data() {
    return {
      ALL_EMITTERS,
      most_used: [],
      objects: [],
      integration: null,
      integration_name: "",
      objects_mapping_types: {},
      object: null,
      is_new_object_type: false,
      mapped_object_type_name: null,
      mapped_object_type: null,
      is_selected_object: false,
      current_mapping: null,
      syncing_is_on: false,
      home_is_ready: false,
      steps: [],
      current_step: "authorization",
      current_step_index: 0
    }
  },

  computed: {
    progress_percentage() {
      return (this.current_step_index / (this.steps.filter((s) => !s.hidden).length - 1)) * 100
    },

    getCurrentComponent() {
      switch (this.current_step) {
        case "authorization":
          return Authorization
        case "upload":
          return SpreadSheetUpload
        case "object-selection":
          return ObjectSelection
        case "mapping-type":
          return MappingType
        case "object-type":
          return ObjectTypeSelect
        case "mapping":
          return Mapping
        default:
          return null
      }
    },
    getCurrentProps() {
      const baseProps = {
        class: "w-full"
      }

      switch (this.current_step) {
        case "object-selection":
          return {
            ...baseProps,
            objects: this.objects,
            integration: this.integration,
            currentMapping: this.current_mapping,
            mostUsedTables: this.most_used
          }
        case "mapping-type":
          return {
            ...baseProps,
            currentMapping: this.current_mapping,
            integration: this.integration,
            mappingType: this.objects_mapping_types[this.object?.name],
            object: this.object
          }
        case "object-type":
          return {
            ...baseProps,
            object: this.object,
            integration: this.integration,
            mappedObjectType: this.mapped_object_type,
            mappingType: this.objects_mapping_types[this.object?.name]
          }
        case "mapping":
          return {
            ...baseProps,
            object: this.object,
            mappedObjectType: this.mapped_object_type,
            isNewObjectType: this.is_new_object_type,
            mappedObjectTypeName: this.mapped_object_type_name,
            integration: this.integration,
            mappingType: this.objects_mapping_types[this.object?.name],
            mapping: this.current_mapping
          }
        default:
          return baseProps
      }
    },
    getCurrentEvents() {
      switch (this.current_step) {
        case "object-selection":
          return {
            objectSelected: (selected_obj) => {
              this.object = selected_obj
              this.current_step = "mapping-type"
            }
          }
        case "mapping-type":
          return {
            editDirectMapping: () => {
              this.mapped_object_type = this.getObjectDirectMappedObjectType(this.object)
              this.objects_mapping_types[this.object.name] = "direct"
              this.current_step = "mapping"
            },
            mappingType: (mapping_type) => {
              this.objects_mapping_types[this.object.name] = mapping_type
              this.current_step = "object-type"
            }
          }
        case "object-type":
          return {
            "update:mappedObjectType": ({ object_type, object_name, is_new_object }) => {
              this.mapped_object_type = object_type
              this.mapped_object_type_name = object_name
              this.is_new_object_type = is_new_object
              this.current_step = "mapping"
            }
          }
        default:
          return {}
      }
    }
  },

  watch: {
    object() {
      this.$utils.replaceItem(this.objects, this.object, "name")
    },
    current_step() {
      // update the status of the steps
      this.steps.forEach((step, index) => {
        if (index < this.steps.findIndex((s) => s.slug == this.current_step)) {
          step.status = "complete"
        } else if (index == this.steps.findIndex((s) => s.slug == this.current_step)) {
          step.status = "current"
        } else {
          step.status = "incomplete"
        }
      })
      this.current_step_index = this.steps.find((s) => s.slug == this.current_step).index
      // add new step to the url without triggering a new navigation
      history.replaceState(
        null,
        null,
        `/admin/integrations/${this.integration_name}/${this.$route.params.integration_reference}/${this.current_step}`
      )
    }
  },

  created() {
    this.steps = [
      {
        name: this.$t("Authorization"),
        slug: "authorization",
        index: 0,
        disabled: false,
        hidden: true,
        description: this.$t("Connect to your") + ` ${this.$route.params.integration_name.capitalize()} ` + this.$t("instance"),
        href: `/admin/integrations/${this.integration_name}/${this.$route.params.integration_reference}/authorization`,
        status: "incomplete"
      },
      {
        name: this.$t("Upload Sheet"),
        slug: "upload",
        index: 0,
        disabled: false,
        hidden: true,
        description: this.$t("Upload your sheet"),
        href: `/admin/integrations/${this.$route.params.integration_name}/${this.$route.params.integration_reference}/upload`
      },
      {
        name: this.$t("Object Selection"),
        slug: "object-selection",
        index: 1,
        description: this.$t("Select the object you want to map"),
        href: `/admin/integrations/${this.$route.params.integration_name}/${this.$route.params.integration_reference}/object-selection`,
        status: "incomplete"
      },
      {
        name: this.$t("Type of Mapping"),
        slug: "mapping-type",
        index: 2,
        description: this.$t("Select the type of mapping you want for your object"),
        href: `/admin/integrations/${this.$route.params.integration_name}/${this.$route.params.integration_reference}/mapping-type`,
        status: "incomplete"
      },
      {
        name: this.$t("Link Objects"),
        slug: "object-type",
        index: 3,
        description: this.$t("Create or link objects"),
        href: `/admin/integrations/${this.$route.params.integration_name}/${this.$route.params.integration_reference}/object-type`,
        status: "incomplete"
      },
      {
        name: this.$t("Map Data"),
        slug: "mapping",
        index: 4,
        description: this.$t("Select what metrics to map into RevSetter"),
        href: `/admin/integrations/${this.$route.params.integration_name}/${this.$route.params.integration_reference}/mapping`,
        status: "incomplete"
      }
    ]

    this.integration_name = this.$route.params.integration_name || ""
    this.register_listeners()
    this.$store.dispatch("ui/showLoading", `Loading ${getServiceTitle(this.integration_name)} objects ...`)
    if (this.$route.params.step == "upload" && this.$route.params.integration_reference == "spreadsheet") {
      this.current_step = "upload"
      this.$store.dispatch("ui/hideLoading")
      this.home_is_ready = true
    } else {
      IntegrationService.getServiceObjects(this.$route.params.integration_reference)
        .then((response) => {
          this.steps[0].status = "complete"
          this.steps[0].description = "Connected"
          this.steps[0].disabled = true
          let step = this.$route.params.step
          let valid_steps = ["upload", "object-selection", "mapping-type", "object-type", "mapping"]
          if (this.$utils.isNotEmpty(step) && valid_steps.includes(step)) {
            this.current_step = step
          } else {
            this.current_step = "object-selection"
          }
          // history.replaceState(null, null, `/admin/integrations/${this.integration_name}/${this.$route.params.integration_reference}/${step}`)
          if (
            (this.current_step == "object-type" || this.current_step == "mapping" || this.current_step == "mapping-type") &&
            !this.object
          ) {
            this.current_step = "object-selection"
          }
          this.integration = response.data.integration
          this.objects = response.data.objects
          this.most_used = response.data.recently_viewed
          this.current_mapping = response.data.mapping
          this.$store.dispatch("ui/hideLoading")
          this.home_is_ready = true
        })
        .catch(() => {
          // authorization is not done
          this.home_is_ready = true
          this.$store.dispatch("ui/hideLoading")
        })
    }
  },

  unmounted() {
    this.unregister_listeners()
  },
  methods: {
    getObjectDirectMappedObjectType(object) {
      return this.current_mapping?.custom_objects.find((o) => o.object_name == object.name).object_type
    },
    goBack() {
      if (this.current_step == "object-type") {
        this.current_step = "mapping-type"
      } else if (this.current_step == "mapping") {
        this.current_step = "object-type"
      } else {
        this.current_step = "object-selection"
      }
    },
    register_listeners() {
      this.$emitter.on(`${ALL_EMITTERS.STOP_SYNCING}${this.integration_name.capitalize()}`, () => {
        this.syncing_is_on = false
      })
      this.$emitter.on(`${ALL_EMITTERS.SELECT_MAPPING_OBJECT}${this.integration_name.capitalize()}`, () => {
        this.is_selected_object = true
        this.current_step = "object-selection"
      })
      this.$emitter.on(`${ALL_EMITTERS.UPDATE_SYNCING}${this.integration_name.capitalize()}`, (status) => {
        this.syncing_is_on = status
      })
    },
    unregister_listeners() {
      this.$emitter.off(`${ALL_EMITTERS.STOP_SYNCING}${this.integration_name.capitalize()}`)
      this.$emitter.off(`${ALL_EMITTERS.SELECT_MAPPING_OBJECT}${this.integration_name.capitalize()}`)
      this.$emitter.off(`${ALL_EMITTERS.UPDATE_SYNCING}${this.integration_name.capitalize()}`)
    },
    cancelIntegration() {
      if (this.$route.query.name == `onboarding-connect-${this.integration_name}`) {
        this.$router.push(`/onboarding/?name=onboarding-skip-${this.integration_name}`)
      } else {
        this.$router.push("/admin/integrations")
      }
    }
  }
}
</script>
